import {getTestProps} from '@benchling/js-test-utils';
import {css, StyleSheet} from 'aphrodite';
import React, {useEffect, useState} from 'react';

import * as Colors from 'ui-library/Colors';
import BenchlingDuotoneIcon from 'ui-library/icons/BenchlingDuotoneIcon';

interface LLMLoadingMessageProps {
  text: string | Array<string>;
  fontSize?: number;
  delay?: number;
}

export function LLMSpinner(): JSX.Element {
  return (
    <BenchlingDuotoneIcon
      icon="spinner-third"
      primaryColor={Colors.PURPLE_DARK}
      secondaryColor={Colors.PURPLE_LIGHTER}
      spin={true}
    />
  );
}

export default function LLMLoadingMessage(props: LLMLoadingMessageProps): JSX.Element {
  const {text, delay = 3000} = props;
  const isArray = Array.isArray(text);
  const textArray = isArray ? text : [text];
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    if (!isArray || textArray.length <= 1) {
      return;
    }

    const lastIndex = textArray.length - 1;
    if (currentIndex >= lastIndex) {
      return;
    }

    const interval = setInterval(() => {
      setCurrentIndex((prev) => {
        const nextIndex = prev + 1;
        if (nextIndex >= lastIndex) {
          clearInterval(interval);
          return lastIndex;
        }
        return nextIndex;
      });
    }, delay);

    return () => {
      clearInterval(interval);
    };
  }, [isArray, textArray.length, currentIndex, delay]);

  const displayText = textArray[currentIndex] || '';

  return (
    <div
      className={css(styles.loadingContainer)}
      {...getTestProps({component: 'LLMLoadingMessage', element: 'root'})}
    >
      <LLMSpinner />
      <span
        className={css(styles.loadingShimmer)}
        // These need to be defined inline to avoid Aphrodite slapping a !important on this
        // and thus ruining the animation
        style={{
          background: `linear-gradient(120deg, ${Colors.GRAY_DARKER} 0%, ${Colors.GRAY_DARK} 20%, ${Colors.GRAY_LIGHT} 40%, ${Colors.GRAY_DARK} 60%, ${Colors.GRAY_DARKER} 80%) 0 0 / 200% 100%`,
          backgroundClip: 'text',
          backgroundSize: '200% 100%',
          WebkitBackgroundClip: 'text',
          fontSize: props.fontSize,
        }}
        {...getTestProps({component: 'SQLAssistantModal', element: 'root'})}
      >
        {displayText}
      </span>
    </div>
  );
}

const keyframes = {
  '0%': {
    backgroundPosition: '200% 0',
  },
  '100%': {
    backgroundPosition: '-200% 0',
  },
};

const styles = StyleSheet.create({
  loadingContainer: {
    alignItems: 'center',
    display: 'flex',
    gap: 8,
  },
  loadingShimmer: {
    animationName: [keyframes],
    animationDelay: '0s',
    animationDuration: '4s',
    animationIterationCount: 'infinite',
    animationTimingFunction: 'linear',
    color: 'transparent',
    display: 'inline-block',
    WebkitTextFillColor: 'transparent',
  },
});
